import React, { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { LocalizationContext } from 'app/localization/Context';

import { CurtainState, DeviceType } from 'app/model/Device';
import { ConnectionDonutChart } from 'app/home/general-components/Charts/ConnectionDonutChart';
import { UserAvatarIcon, shouldUserPNInsteadOfNameForUser } from 'app/home/overlay-pages/setting/UsersAndHome';
import { markupMobilePhoneNumberWithCountryCode } from 'app/home/general-components/NumberInputHelper';
import { UsageBarChart } from 'app/home/general-components/Charts/UsageBarChart';
import { ScatterChart, convertValuesToScatterChartDataPointList } from 'app/home/general-components/Charts/ScatterChart';
import { mergeBounds, prepareLineChartWithGapFromTimestampValue } from 'app/home/general-components/Charts/PowerChart';
import { BriefLineChart, ColorConfig } from 'app/home/general-components/Charts/BriefLineChart';

import { withRandomIds } from 'images/withRandomIds';

import { ReactComponent as _IconReports } from 'images/report/report.svg';

import { ReactComponent as _IconMotion } from 'images/bind/element/motion-detected.svg';
import { ReactComponent as _IconDoor } from 'images/bind/element/door-closed.svg';
import { ReactComponent as _IconSound } from 'images/device/state-chart-icon/sound.svg';
import { ReactComponent as _IconPlug } from 'images/device/status-icon/plug-on.svg';
import { ReactComponent as _IconLamp1 } from 'images/device/status-icon/lamp-on-1.svg';
import { ReactComponent as _IconLamp2 } from 'images/device/status-icon/lamp-on-2.svg';
import { ReactComponent as _IconLamp3 } from 'images/device/status-icon/lamp-on-3.svg';
import { ReactComponent as _IconLamp4 } from 'images/device/status-icon/lamp-on-4.svg';
import { ReactComponent as _IconRelay1 } from 'images/device/status-icon/relay-on-1.svg';
import { ReactComponent as _IconRelay2 } from 'images/device/status-icon/relay-on-2.svg';
import { ReactComponent as _IconRelay3 } from 'images/device/status-icon/relay-on-3.svg';
import { ReactComponent as _IconRelay4 } from 'images/device/status-icon/relay-on-4.svg';
import { ReactComponent as _IconCoolerPower } from 'images/device/status-icon/cooler-power-on.svg';
import { ReactComponent as _IconPump } from 'images/device/status-icon/cooler-pump-on.svg';
import { ReactComponent as _IconSlowFan } from 'images/device/status-icon/cooler-slow-on.svg';
import { ReactComponent as _IconFastFan } from 'images/device/status-icon/cooler-fast-on.svg';
import { ReactComponent as _IconCurtainOpen1 } from 'images/device/status-icon/curtain-open-1.svg';
import { ReactComponent as _IconCurtainClose1 } from 'images/device/status-icon/curtain-close-1.svg';
import { ReactComponent as _IconCurtainOpen2 } from 'images/device/status-icon/curtain-open-2.svg';
import { ReactComponent as _IconCurtainClose2 } from 'images/device/status-icon/curtain-close-2.svg';
import { ReactComponent as _IconWCFanOn } from 'images/device/status-icon/wc-fan-on.svg';
import { ReactComponent as _IconWCSprayOn } from 'images/device/status-icon/wc-spray-on.svg';
import { ReactComponent as _IconWCLampOn } from 'images/device/status-icon/wc-lamp-on.svg';
import { ReactComponent as _IconWCMusicOn } from 'images/device/status-icon/wc-music-on.svg';

import momentj from 'moment-jalaali';

import './InsightsAndCharts.scss'

const IconReports = withRandomIds(_IconReports);
const IconMotion = withRandomIds(_IconMotion);
const IconDoor = withRandomIds(_IconDoor);
const IconSound = withRandomIds(_IconSound);
const IconPlug = withRandomIds(_IconPlug);
const IconLamp1 = withRandomIds(_IconLamp1);
const IconLamp2 = withRandomIds(_IconLamp2);
const IconLamp3 = withRandomIds(_IconLamp3);
const IconLamp4 = withRandomIds(_IconLamp4);
const IconRelay1 = withRandomIds(_IconRelay1);
const IconRelay2 = withRandomIds(_IconRelay2);
const IconRelay3 = withRandomIds(_IconRelay3);
const IconRelay4 = withRandomIds(_IconRelay4);
const IconCoolerPower = withRandomIds(_IconCoolerPower);
const IconPump = withRandomIds(_IconPump);
const IconSlowFan = withRandomIds(_IconSlowFan);
const IconFastFan = withRandomIds(_IconFastFan);
const IconCurtainOpen1 = withRandomIds(_IconCurtainOpen1);
const IconCurtainClose1 = withRandomIds(_IconCurtainClose1);
const IconCurtainOpen2 = withRandomIds(_IconCurtainOpen2);
const IconCurtainClose2 = withRandomIds(_IconCurtainClose2);
const IconWCFanOn = withRandomIds(_IconWCFanOn);
const IconWCSprayOn = withRandomIds(_IconWCSprayOn);
const IconWCLampOn = withRandomIds(_IconWCLampOn);
const IconWCMusicOn = withRandomIds(_IconWCMusicOn);

export default function InsightsAndCharts({
    devices, allUsers,
    insights, allLampsChart, stateChartPerMac,
    windowWidth }) {
    const hs = useContext(HistoryStackContext);
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const { cell1Width, cell2Width, cellWideWidth, numOfWideCellsInARow } = useMemo(() => {
        const useableWidth = windowWidth - 16;
        const numOfWideCellsInARow = Math.max(1, Math.floor(useableWidth / 360));
        const cellWideWidth = (useableWidth - 8 * (numOfWideCellsInARow - 1)) / numOfWideCellsInARow;
        const numOfCell1InAWideCell = Math.max(2, Math.min(4, Math.floor(cellWideWidth / 120)));
        const cell1Width = (cellWideWidth - 8 * (numOfCell1InAWideCell - 1)) / numOfCell1InAWideCell;
        const cell2Width = cell1Width * 2 + 8;
        return { cell1Width, cell2Width, cellWideWidth, numOfWideCellsInARow };
    }, [windowWidth]);

    const { doorState, motionState, lightState, connectionState } = useMemo(() => {
        let doorState = { on: 0, off: 0 };
        let motionState = { on: 0, off: 0 };
        let lightState = { on: 0, off: 0 };
        for (let i = 0; i < devices.length; i++) {
            const device = devices[i];
            if (device.active) {
                switch (device.type) {
                    case DeviceType.doorSensor:
                    case DeviceType.wifiDoorSensor:
                        if (device.state) { doorState.on += 1; } else { doorState.off += 1; }
                        break;
                    case DeviceType.motion:
                    case DeviceType.wifiMotionSensor:
                        if (device.state) { motionState.on += 1; } else { motionState.off += 1; }
                        break;
                    case DeviceType.multipurposeSensor:
                        if (device.motion) { motionState.on += 1; } else { motionState.off += 1; }
                        break;
                    case DeviceType.lamp:
                    case DeviceType.lampX1TwoWay:
                        if (device.state) { lightState.on += 1; } else { lightState.off += 1; }
                        break;
                    case DeviceType.dpst:
                        if (device.state1) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.state2) { lightState.on += 1; } else { lightState.off += 1; }
                        break;
                    case DeviceType.lampX3:
                        if (device.state1) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.state2) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.state3) { lightState.on += 1; } else { lightState.off += 1; }
                        break;
                    case DeviceType.lampX4:
                        if (device.state1) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.state2) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.state3) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.state4) { lightState.on += 1; } else { lightState.off += 1; }
                        break;
                    case DeviceType.curtain1Lamp2:
                        if (device.lamp1) { lightState.on += 1; } else { lightState.off += 1; }
                        if (device.lamp2) { lightState.on += 1; } else { lightState.off += 1; }
                        break;
                    default:
                        break;
                }
            }
        }
        let connectionState = {
            connected: devices.filter(device => device.active).length,
            disconnected: devices.filter(device => !device.active).length,
        }
        return { doorState, motionState, lightState, connectionState };
    }, [devices]);

    const numberOfUsers = allUsers.joinedUsers.length;

    let { automation_trigger, scenario_run, device_change_sources, active_users } = insights;

    const anyDataInDeviceChangeSources = useMemo(() => {
        if (device_change_sources !== undefined) {
            const { scenario, automation, manual, application } = device_change_sources;
            const all = scenario + automation + manual + application;
            return all > 0;
        } else {
            return false;
        }
    }, [device_change_sources]);

    const summaryViewDataList = useMemo(() => {
        const _summaryViewDataList = [];
        if ((doorState.on + doorState.off) > 0) {
            const doorView = { color: "", key: "doorView" };
            if (doorState.on === 0) { // all closed
                doorView.color = "green";
                doorView.bigText = s.insightsSummary.all;
                doorView.normalText = s.insightsSummary.doors;
                doorView.smallText = s.insightsSummary.doorsClosed;
            } else {
                doorView.color = "red";
                doorView.bigText = l.localizeNumber(doorState.on);
                doorView.normalText = s.insightsSummary.door(doorState.on);
                doorView.smallText = s.insightsSummary.doorOpen(doorState.on);
            }
            _summaryViewDataList.push(doorView);
        }
        if ((motionState.on + motionState.off) > 0) {
            const motionView = { color: "", key: "motionView" };
            if (motionState.on === 0) { // all idle
                motionView.color = "green";
                motionView.bigText = s.insightsSummary.all;
                motionView.normalText = s.insightsSummary.motions;
                motionView.smallText = s.insightsSummary.motionsIdle;
            } else {
                motionView.color = "red";
                motionView.bigText = l.localizeNumber(motionState.on);
                motionView.normalText = s.insightsSummary.motion(motionState.on);
                motionView.smallText = s.insightsSummary.motionDetecting(motionState.on);
            }
            _summaryViewDataList.push(motionView);
        }
        if ((lightState.on + lightState.off) > 0) {
            const lightView = { color: "", key: "lightView" };
            if (lightState.on === 0) { // all off
                lightView.color = "";
                lightView.bigText = s.insightsSummary.all;
                lightView.normalText = s.insightsSummary.lights;
                lightView.smallText = s.insightsSummary.lightOff;
            } else {
                lightView.color = "yellow";
                lightView.bigText = l.localizeNumber(lightState.on);
                lightView.normalText = s.insightsSummary.light(lightState.on);
                lightView.smallText = s.insightsSummary.lightOn(lightState.on);
            }
            _summaryViewDataList.push(lightView);
        }
        if (automation_trigger !== undefined) {
            const automationView = { color: "", key: "automationView" };
            automationView.bigText = l.localizeNumber(automation_trigger.count);
            automationView.normalText = s.insightsSummary.automation(automation_trigger.count);
            automationView.smallText = l.localizeNumber(s.insightsSummary.automationTriggered(automation_trigger.duration));
            _summaryViewDataList.push(automationView);
        }
        if (scenario_run !== undefined) {
            const scenarioView = { color: "", key: "scenarioView" };
            scenarioView.bigText = l.localizeNumber(scenario_run.count);
            scenarioView.normalText = s.insightsSummary.scene(scenario_run.count);
            scenarioView.smallText = l.localizeNumber(s.insightsSummary.sceneRan(scenario_run.duration));
            _summaryViewDataList.push(scenarioView);
        }
        if (numberOfUsers > 0) {
            const userView = { color: "", key: "userView" };
            userView.bigText = l.localizeNumber(numberOfUsers);
            userView.normalText = s.insightsSummary.user(numberOfUsers);
            userView.smallText = s.insightsSummary.usersHaveAccess(numberOfUsers);
            _summaryViewDataList.push(userView);
        }
        return _summaryViewDataList;
    }, [l, s, doorState, motionState, lightState,
        automation_trigger, scenario_run, numberOfUsers]);

    const deviceChartViewDataList = useMemo(() => {
        const _deviceChartViewDataList = [];
        if (stateChartPerMac !== undefined) {
            for (let i = 0; i < devices.length; i++) {
                const device = devices[i];
                const stateChart = stateChartPerMac[device.mac];
                if (stateChart !== undefined) {
                    switch (device.type) {
                        // charts in the insight page
                        case DeviceType.doorSensor:
                        case DeviceType.motion:
                        case DeviceType.wifiDoorSensor:
                        case DeviceType.wifiMotionSensor:
                        case DeviceType.multipurposeSensor:
                        case DeviceType.hinavaEcoThermostat:
                        default:
                            break;
                    }
                    _deviceChartViewDataList.push({
                        device: {
                            mac: device.mac,
                            name: device.name,
                            roomName: device.roomName,
                            type: device.type,
                            num_channels: device.num_channels,
                        },
                        stateChart
                    });
                }
            }
        }
        return _deviceChartViewDataList;
    }, [stateChartPerMac, devices]);

    const anyLampInHome = useMemo(() => {
        return devices.filter(device => [
            DeviceType.lamp,
            DeviceType.lampX1TwoWay,
            DeviceType.dpst,
            DeviceType.lampX3,
            DeviceType.lampX4,
            DeviceType.curtain1Lamp2,
        ].includes(device.type)).length > 0;
    }, [devices]);

    const anyDataInSummarySection = (summaryViewDataList.length > 0) || (connectionState.connected + connectionState.disconnected > 0);
    const anyDataInAppSection = (anyDataInDeviceChangeSources && (device_change_sources !== undefined)) || ((active_users !== undefined) && (numberOfUsers > 0));
    const anyDataInDeviceSection = (anyLampInHome && (allLampsChart !== undefined)) || ((deviceChartViewDataList !== undefined) && (deviceChartViewDataList.length > 0));

    // console.log("allLampsChart", allLampsChart); // duration: 7, values: [288, 250, 300]
    // console.log("stateChartPerMac", stateChartPerMac); // mac: {state: [[1720788781, 1], [1720875094, 0], [1720875181, null]], currentTimestamp: 1720875181615}

    return (
        <div className="insights-and-charts-container" style={{
            "--cell1-width": String(cell1Width) + "px",
            "--cell2-width": String(cell2Width) + "px",
            "--cell-wide-width": String(cellWideWidth) + "px",
        }}>
            <div className="section">
                <div className="header">
                    <div className="label">{s.insightsSummary.title}</div>
                    <div />
                    <div className="button-border" onClick={() => { hs.push("/reports/detailed-reports"); }}>
                        <div className="label">
                            {s.insightsSummary.detailedReports}
                        </div>
                        <IconReports className="icon" />
                    </div>
                </div>
                {anyDataInSummarySection ?
                    <div className="content summary">
                        {summaryViewDataList.map(viewData => <InsightDataCell key={"InsightDataCell-" + viewData.key} viewData={viewData} />)}
                        <ConnectedDeviceChart connectionState={connectionState} l={l} cell1Width={cell1Width} />
                    </div> :
                    <div className="empty-list-cell">{s.dataIsLoading}</div>}
            </div>
            {anyDataInAppSection ? <div className="section margin-top">
                <div className="header">
                    <div className="label">{s.appReports.title}</div>
                </div>
                <div className="content app">
                    <SourceOfChanges secondOrder={numOfWideCellsInARow > 1} l={l} device_change_sources={device_change_sources} />
                    <MostActiveUsers l={l} active_users={active_users} joinedUsers={allUsers.joinedUsers} />
                </div>
            </div> : null}
            {anyDataInDeviceSection ? <div className="section margin-top">
                <div className="header">
                    <div className="label">{s.deviceReport.title}</div>
                </div>
                <div className="content device">
                    <DeviceStateCharts l={l} cellWideWidth={cellWideWidth} deviceChartViewDataList={deviceChartViewDataList} />
                </div>
                {anyLampInHome ? <div className="content device">
                    <AllLampsChart l={l} cellWideWidth={cellWideWidth} allLampsChart={allLampsChart} />
                </div> : null}
            </div> : null}
            <div style={{ height: 16 }} />
        </div>
    )
}

function SourceOfChanges({ l, device_change_sources, secondOrder }) {
    const s = l.strings;
    if (device_change_sources !== undefined) {
        const { scenario, automation, manual, application, duration } = device_change_sources;
        const all = scenario + automation + manual + application;
        if (all > 0) {
            const [scenarioP, automationP, manualP, applicationP] =
                [scenario, automation, manual, application].map(x => Math.round(x / all * 100));

            return (
                <div className="wide-item source-of-changes" style={secondOrder ? { order: 2 } : undefined}>
                    <div className="info">{l.localizeNumber(s.appReports.sourceOfChanges(duration))}</div>
                    <div className="chart" style={{
                        gridTemplateColumns:
                            `${scenarioP}fr ${automationP}fr ${manualP}fr ${applicationP}fr`
                    }}>
                        <div className="blue-1"></div>
                        <div className="blue-2"></div>
                        <div className="yellow"></div>
                        <div className="green"></div>
                    </div>
                    <div className="labels">
                        {(scenarioP > 0) ?
                            <div className="label-h blue-1">
                                {l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="big-text">{l.localizeNumber(scenarioP)}</div>
                                {!l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="normal-text">{s.appReports.scenes}</div>
                            </div> : null}
                        {(automationP > 0) ?
                            <div className="label-h blue-2">
                                {l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="big-text">{l.localizeNumber(automationP)}</div>
                                {!l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="normal-text">{s.appReports.automations}</div>
                            </div> : null}
                        {(manualP > 0) ?
                            <div className="label-h yellow">
                                {l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="big-text">{l.localizeNumber(manualP)}</div>
                                {!l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="normal-text">{s.appReports.manual}</div>
                            </div> : null}
                        {(applicationP > 0) ?
                            <div className="label-h green">
                                {l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="big-text">{l.localizeNumber(applicationP)}</div>
                                {!l.isRTL ? <div className="pretty-big-text">{s.appReports.percent}</div> : null}
                                <div className="normal-text">{s.appReports.application}</div>
                            </div> : null}
                    </div>
                </div>
            )
        } else {
            return null;
        }
    } else {
        return null;
    }
}


function MostActiveUsers({ l, active_users, joinedUsers }) {
    const s = l.strings;

    const viewData = useMemo(() => {
        const _viewData = [];
        if (active_users !== undefined && active_users.users !== undefined) {
            for (let i = 0; i < active_users.users.length; i++) {
                const { user_id, visits, commands } = active_users.users[i];
                const _user_list = joinedUsers.filter(u => u["user_id"] === user_id);
                if (_user_list.length > 0) {
                    const _user = _user_list[0];
                    _viewData.push({
                        user_id: user_id,
                        name: _user["name"],
                        avatar: _user["avatar"],
                        phone_number: _user["phone_number"],
                        visits, commands
                    })
                }
            }
        }
        return _viewData;
    }, [active_users, joinedUsers]);

    const duration = active_users ? active_users.duration : undefined;

    if (viewData.length > 0) {
        return (
            <div className="wide-item most-active-users">
                <div className="info">{l.localizeNumber(s.appReports.mostActiveUsers(viewData.length, duration))}</div>
                {viewData.map(user => (
                    <div className="user" key={"MostActiveUsers-" + user.user_id}>
                        <UserAvatarIcon avatarId={user.avatar} className="avatar" />
                        {shouldUserPNInsteadOfNameForUser(user) ?
                            <div className="name empty">{l.localizeNumber(markupMobilePhoneNumberWithCountryCode(user.phone_number))}</div> :
                            <div className="name">{user.name}</div>}
                        <div />
                        <div className="stats">
                            <div className="label-h">
                                <div className="big-text">{l.localizeNumber(user.visits)}</div>
                                <div className="normal-text">{s.appReports.visit(user.visits) + s.appReports.comma}</div>
                            </div>
                            <div className="label-h">
                                <div className="big-text">{l.localizeNumber(user.commands)}</div>
                                <div className="normal-text">{s.appReports.command(user.commands)}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )
    } else {
        return null;
    }
}

function InsightDataCell({ viewData }) {
    return (
        <div className={"item " + (viewData.color ? viewData.color : "")}>
            {viewData.bigText ? <div className="summary-big-text">{viewData.bigText}</div> : null}
            {viewData.normalText ? <div className="summary-normal-text">{viewData.normalText}</div> : null}
            {viewData.smallText ? <div className="summary-small-text">{viewData.smallText}</div> : null}
        </div>
    );
}

function ConnectedDeviceChart({ l, cell1Width, connectionState: { connected, disconnected } }) {
    const s = l.strings;

    if (connected + disconnected > 0) {
        const chartWidth = cell1Width - 32;
        return (
            <div className="connection-item">
                <div className="side-labels">
                    <div className="label-h green">
                        <div className="big-text">{l.localizeNumber(connected)}</div>
                        <div className="normal-text">{s.insightsSummary.connectedDevice(connected)}</div>
                    </div>
                    {disconnected > 0 ? <div className="label-h red">
                        <div className="big-text">{l.localizeNumber(disconnected)}</div>
                        <div className="normal-text">{s.insightsSummary.disconnectedDevice(disconnected)}</div>
                    </div> : null}
                </div>
                <div className="chart">
                    <div className="label-v">
                        <div className="big-text">{l.localizeNumber(connected + disconnected)}</div>
                        <div className="normal-text">{s.insightsSummary.totalDevice(connected + disconnected)}</div>
                    </div>
                    <ConnectionDonutChart
                        chartKey="connection-donut-svg" className="connection-donut-svg"
                        width={chartWidth}
                        height={chartWidth}
                        lineWidth={5 / 100 * cell1Width}
                        numberOfConnected={connected} numberOfDisconnected={disconnected}
                    />
                </div>
            </div>
        );
    } else {
        return null;
    }
}

function AllLampsChart({ l, cellWideWidth, allLampsChart }) {
    const s = l.strings;

    const valueFormatter = useCallback((v) => {
        if (v >= 1) {
            return `${l.localizeNumber(v.toFixed())} ${s.deviceReport.hour(v.toFixed())}`
        } else {
            return `${l.localizeNumber(v)} ${s.deviceReport.hour(v.toFixed())}`
        }
    }, [l, s]);

    if ((allLampsChart !== undefined) && (allLampsChart.values.length === 3)) {
        const chartWidth = cellWideWidth - 16;
        return (
            <div className="all-lamps-item wide-item">
                <div className="title">{s.deviceReport.allLamps}</div>
                <UsageBarChart
                    chartKey="all-lamps-chart" className="all-lamps-chart-svg"
                    width={chartWidth}
                    barHeight={65} barWidth={24}
                    color="#FFD94D"
                    data={[
                        { value: allLampsChart.values[0], label: s.deviceReport.twoWeeksAgo },
                        { value: allLampsChart.values[1], label: s.deviceReport.lastWeek },
                        { value: allLampsChart.values[2], label: s.deviceReport.thisWeek },

                    ]}
                    valueFormat={valueFormatter}
                />
                <div className="info label-h yellow">
                    <div className="normal-text">{s.deviceReport.allLampsInfoPrefix(allLampsChart.values[0])}</div>
                    <div className="big-text">{l.localizeNumber(allLampsChart.values[2])}</div>
                    <div className="normal-text">{s.deviceReport.allLampsInfoPostfix(allLampsChart.values[0])}</div>
                </div>
            </div>
        );
    } else {
        return null;
    }
}


function DeviceStateCharts({ l, cellWideWidth, deviceChartViewDataList }) {
    const tempValueFormatter = useCallback((v) => l.localizeNumber(`${v}°`), [l]);

    const views = []
    for (let i = 0; i < deviceChartViewDataList.length; i++) {
        const viewData = deviceChartViewDataList[i];
        switch (viewData.device.type) {
            case DeviceType.doorSensor:
            case DeviceType.motion:
            case DeviceType.wifiDoorSensor:
            case DeviceType.wifiMotionSensor:
                views.push(<DeviceScatterView
                    l={l} cellWideWidth={cellWideWidth} key={"device-scatter-chart-component-for-" + viewData.device.mac}
                    device={viewData.device} stateChart={viewData.stateChart} />)
                break;
            case DeviceType.multipurposeSensor:
                views.push(<DeviceScatterView
                    l={l} cellWideWidth={cellWideWidth} key={"device-scatter-chart-component-for-" + viewData.device.mac}
                    device={viewData.device} stateChart={viewData.stateChart} />)
                views.push(<DeviceBriefLineChartView
                    l={l} cellWideWidth={cellWideWidth} key={"device-temp-chart-component-for-" + viewData.device.mac}
                    device={viewData.device} stateChart={viewData.stateChart}
                    valueFormatter={tempValueFormatter} />)
                break;
            case DeviceType.hinavaEcoThermostat:
                views.push(<DeviceBriefLineChartView
                    l={l} cellWideWidth={cellWideWidth} key={"device-temp-chart-component-for-" + viewData.device.mac}
                    device={viewData.device} stateChart={viewData.stateChart}
                    valueFormatter={tempValueFormatter} />)
                break;
            default:
                break;
        }
    }
    return <Fragment>
        {views}
    </Fragment>;
}

function DeviceBriefLineChartView({
    l, cellWideWidth, valueFormatter,
    device: { mac, name, roomName, type }, stateChart }) {

    const lineConfigs = useMemo(() => {
        switch (type) {
            case DeviceType.multipurposeSensor:
                return [{
                    stateKey: "temperature",
                    colorConfig: ColorConfig.blueWithGrad,
                    labelName: "envTemperature",
                }]
            case DeviceType.hinavaEcoThermostat:
                return [{
                    stateKey: "set_point",
                    colorConfig: ColorConfig.yellow,
                    labelName: "setPoint",
                },
                {
                    stateKey: "env_temperature",
                    colorConfig: ColorConfig.blueWithGrad,
                    labelName: "envTemperature",
                }]
            default:
                break;
        }
    }, [type]);

    return (
        <div className="device-item wide-item">
            <div className="name">{name}</div>
            <div className="room-name">{roomName !== undefined ? roomName : ""}</div>
            <DeviceBriefLineChartViewContent
                l={l} chartWidth={cellWideWidth - 16} stateChart={stateChart}
                chartKey={"device-temp-chart-for-" + mac} lineConfigs={lineConfigs}
                valueFormatter={valueFormatter} />
        </div>
    );
}

export function DeviceBriefLineChartViewContent({
    l, chartWidth, chartKey, stateChart, valueFormatter, marginLeft,
    lineConfigs // a list of {stateKey, colorConfig, labelName}
}) {
    const _marginLeft = (marginLeft === undefined) ? 32 : marginLeft;
    const s = l.strings;

    const [startTime, endTime, gaps, dateBound, valueBound, linesList, dotsList, colorConfigList, labelNames] = useMemo(() => {
        let _startTime = undefined;
        let _endTime = undefined;
        let _gaps = [];
        let _dateBound = { min: undefined, max: undefined };
        let _valueBound = { min: undefined, max: undefined };
        let _linesList = [];
        let _dotsList = [];
        let _colorConfigList = [];
        let _labelNames = [];
        if (lineConfigs.length === 1) {
            if (stateChart[lineConfigs[0].stateKey] !== undefined) {
                const [dots1, lines1, gaps1, dateBound1, valueBound1] =
                    prepareLineChartWithGapFromTimestampValue(stateChart[lineConfigs[0].stateKey], null);
                _gaps = gaps1;
                _dateBound = dateBound1;
                _valueBound = valueBound1;
                _linesList = [lines1];
                _dotsList = [dots1];
                _startTime = _dateBound.min;
                _endTime = _dateBound.max;
                _colorConfigList = [lineConfigs[0].colorConfig];
                _labelNames = [lineConfigs[0].labelName];
            } else {
                console.log("DeviceBriefLineChartViewContent undefined for", lineConfigs[0].stateKey, stateChart);
            }
        } else if (lineConfigs.length === 2) {
            if ((stateChart[lineConfigs[0].stateKey] !== undefined) &&
                (stateChart[lineConfigs[1].stateKey] !== undefined)) {
                const [dots1, lines1, gaps1, dateBound1, valueBound1] =
                    prepareLineChartWithGapFromTimestampValue(stateChart[lineConfigs[0].stateKey], null);
                const [dots2, lines2, _, dateBound2, valueBound2] = // eslint-disable-line no-unused-vars
                    prepareLineChartWithGapFromTimestampValue(stateChart[lineConfigs[1].stateKey], null);

                _gaps = gaps1;
                _dateBound = mergeBounds(dateBound1, dateBound2);
                _valueBound = mergeBounds(valueBound1, valueBound2);
                _linesList = [lines2, lines1];
                _dotsList = [dots2, dots1];
                _startTime = _dateBound.min;
                _endTime = _dateBound.max;
                _colorConfigList = [lineConfigs[0].colorConfig, lineConfigs[1].colorConfig];
                _labelNames = [lineConfigs[0].labelName, lineConfigs[1].labelName];
            } else {
                console.log("DeviceBriefLineChartViewContent undefined [0] for", lineConfigs[0].stateKey, stateChart);
                console.log("DeviceBriefLineChartViewContent undefined [1] for", lineConfigs[1].stateKey, stateChart);
            }
        }
        return [_startTime, _endTime, _gaps, _dateBound, _valueBound, _linesList, _dotsList, _colorConfigList, _labelNames];
    }, [stateChart, lineConfigs]);

    const startTimeStr = useMemo(() => {
        const m = momentj.unix(startTime);
        if (l.language === 'en') {
            momentj.locale("en")
            return [l.localizeNumber(m.format("DD MMMM YYYY")), l.localizeNumber(m.format("HH:mm"))];
        } else if (l.language === 'fa') {
            momentj.locale("fa")
            return [l.localizeNumber(m.format("jDD jMMMM jYYYY")), l.localizeNumber(m.format("HH:mm"))];
        } else if (l.language === 'ar') {
            momentj.locale("ar")
            return [l.localizeNumber(m.format("DD MMMM YYYY")), l.localizeNumber(m.format("HH:mm"))];
        }
    }, [l, startTime]);

    return (
        <Fragment>
            <div className="main-charts">
                <CursorView l={l} startTime={startTime} endTime={endTime} marginLeft={_marginLeft} marginRight={8} />  {/*  search BriefLineChart-margin-right */}
                <BriefLineChart
                    chartKey={chartKey} className="temp-chart-svg"
                    width={chartWidth} height={75}
                    gaps={gaps} linesList={linesList} dotsList={dotsList}
                    valueFormatter={valueFormatter} colorConfigList={colorConfigList}
                    dateBound={dateBound} valueBound={valueBound} smooth={true} marginLeft={_marginLeft}
                />
                <div className="time-labels" style={{ marginLeft: _marginLeft }}>
                    <div className="normal-text">{startTimeStr[1]}</div>
                    <div className="normal-text">{startTimeStr[0]}</div>
                    <div />
                    <div className="normal-text">{s.deviceReport.now}</div>
                </div>
            </div>
            <div />
            <div className="line-name-labels">
                {labelNames.map((name, i) =>
                    <div key={"label-for-" + name} className="label" style={{ color: colorConfigList[i].line }}>
                        <div className="line" style={{ backgroundColor: colorConfigList[i].line }}>
                            <div className="grad-shadow" style={{ background: colorConfigList[i].gradStr }}>
                            </div>
                        </div>
                        <div className="normal-text">{s.deviceReport[name]}</div>
                    </div>
                )}
            </div>
        </Fragment>
    );
}

function DeviceScatterView({
    l, cellWideWidth,
    device: { mac, name, roomName, type, num_channels }, stateChart }) {

    return (
        <div className="device-item wide-item">
            <div className="name">{name}</div>
            <div className="room-name">{roomName !== undefined ? roomName : ""}</div>
            <DeviceScatterViewContent l={l} chartWidth={cellWideWidth - 16} device={{ mac, type, num_channels }} stateChart={stateChart} />
        </div>
    );
}

function addScatterViewChartData({ stateKey, stateChart, chartKey, color, colorName, labelName, icon, _chartDataList, valueReplacer }) {
    const values = stateChart[stateKey];
    if (values !== undefined) {
        const [dataPoints, numberOfChanges, dataLength] = convertValuesToScatterChartDataPointList(values, valueReplacer);
        _chartDataList.push({
            key: chartKey,
            color: color, colorName: colorName,
            count: numberOfChanges,
            labelName: labelName,
            data: dataPoints,
            dataLength: dataLength,
            icon: icon,
        });
        let _startTime = undefined;
        let _endTime = undefined;
        if (values.length > 0) {
            _startTime = values[0][0];
            _endTime = values[values.length - 1][0];
        }
        return [_startTime, _endTime];
    } else {
        console.log("addScatterViewChartData undefined values for", stateChart, stateKey);
        return [undefined, undefined]
    }
}

export function DeviceScatterViewContent({
    l, chartWidth,
    device: { mac, type, num_channels }, stateChart }) {
    const s = l.strings;

    const [chartDataList, startTime, endTime] = useMemo(() => {
        const _chartDataList = [];
        let _startTime = undefined;
        let _endTime = undefined;
        switch (type) {
            case DeviceType.motion:
            case DeviceType.wifiMotionSensor:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state-" + mac,
                    color: "#FFD94D",
                    labelName: "motionDetected", colorName: "yellow",
                    icon: IconMotion,
                });
                break;
            case DeviceType.doorSensor:
            case DeviceType.wifiDoorSensor:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state-" + mac,
                    color: "#FFD94D",
                    labelName: "opened", colorName: "yellow",
                    icon: IconDoor,
                });
                break;
            case DeviceType.multipurposeSensor:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "motion", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-motion-" + mac,
                    color: "#FFD94D",
                    labelName: "motionDetected", colorName: "yellow",
                    icon: IconMotion,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "sound", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-sound-" + mac,
                    color: "#39E4EF",
                    labelName: "soundDetected", colorName: "blue-1",
                    icon: IconSound,
                });
                break;
            case DeviceType.plug:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconPlug,
                });
                break;
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp1,
                });
                break;
            case DeviceType.dpst:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state1-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp1,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state2", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state2-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp2,
                });
                break;
            case DeviceType.lampX3:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state1-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp1,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state2", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state2-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp2,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state3", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state3-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp3,
                });
                break;
            case DeviceType.lampX4:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state1-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp1,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state2", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state2-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp2,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state3", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state3-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp3,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "state4", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-state4-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp4,
                });
                break;
            case DeviceType.curtain1Lamp2:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "lamp1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-lamp1-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp1,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "lamp2", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-lamp2-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconLamp2,
                });
                const curtainOpenR_ = (x) => {
                    if (x === CurtainState.open) {
                        return true;
                    } else if ((x === CurtainState.close) || (x === CurtainState.idle)) {
                        return false;
                    } else {
                        return x;
                    }
                }
                const curtainCloseR_ = (x) => {
                    if (x === CurtainState.close) {
                        return true;
                    } else if ((x === CurtainState.open) || (x === CurtainState.idle)) {
                        return false;
                    } else {
                        return x;
                    }
                }
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "curtain", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-curtain-open-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconCurtainOpen1,
                    valueReplacer: curtainOpenR_,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "curtain", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-curtain-close-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconCurtainClose1,
                    valueReplacer: curtainCloseR_,
                });
                break;
            case DeviceType.relay4:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "channel1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-channel1-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconRelay1,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "channel2", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-channel2-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconRelay2,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "channel3", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-channel3-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconRelay3,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "channel4", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-channel4-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconRelay4,
                });
                break;
            case DeviceType.curtain:
                const curtainOpenR = (x) => {
                    if (x === CurtainState.open) {
                        return true;
                    } else if ((x === CurtainState.close) || (x === CurtainState.idle)) {
                        return false;
                    } else {
                        return x;
                    }
                }
                const curtainCloseR = (x) => {
                    if (x === CurtainState.close) {
                        return true;
                    } else if ((x === CurtainState.open) || (x === CurtainState.idle)) {
                        return false;
                    } else {
                        return x;
                    }
                }
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "channel1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-channel1-open-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconCurtainOpen1,
                    valueReplacer: curtainOpenR,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "channel1", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-channel1-close-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconCurtainClose1,
                    valueReplacer: curtainCloseR,
                });
                if (num_channels > 1) {
                    [_startTime, _endTime] = addScatterViewChartData({
                        stateKey: "channel2", stateChart, _chartDataList,
                        chartKey: "device-scatter-chart-for-channel2-open-" + mac,
                        color: "#06D4B9",
                        labelName: undefined, colorName: undefined,
                        icon: IconCurtainOpen2,
                        valueReplacer: curtainOpenR,
                    });
                    [_startTime, _endTime] = addScatterViewChartData({
                        stateKey: "channel2", stateChart, _chartDataList,
                        chartKey: "device-scatter-chart-for-channel2-close-" + mac,
                        color: "#06D4B9",
                        labelName: undefined, colorName: undefined,
                        icon: IconCurtainClose2,
                        valueReplacer: curtainCloseR,
                    });
                }
                break;
            case DeviceType.evaporativeCooler:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "power", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-power-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconCoolerPower,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "pump", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-pump-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconPump,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "slow_fan", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-slow_fan-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconSlowFan,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "fast_fan", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-fast_fan-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconFastFan,
                });
                break;
            case DeviceType.wc:
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "lamp", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-lamp-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconWCLampOn,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "fan", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-fan-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconWCFanOn,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "spray", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-spray-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconWCSprayOn,
                });
                [_startTime, _endTime] = addScatterViewChartData({
                    stateKey: "music", stateChart, _chartDataList,
                    chartKey: "device-scatter-chart-for-music-" + mac,
                    color: "#06D4B9",
                    labelName: undefined, colorName: undefined,
                    icon: IconWCMusicOn,
                });
                break;
            default:
                break;
        }
        return [_chartDataList, _startTime, _endTime];
    }, [stateChart, type, num_channels, mac]);

    const startTimeStr = useMemo(() => {
        const m = momentj.unix(startTime);
        if (l.language === 'en') {
            momentj.locale("en")
            return [l.localizeNumber(m.format("DD MMMM YYYY")), l.localizeNumber(m.format("HH:mm"))];
        } else if (l.language === 'fa') {
            momentj.locale("fa")
            return [l.localizeNumber(m.format("jDD jMMMM jYYYY")), l.localizeNumber(m.format("HH:mm"))];
        } else if (l.language === 'ar') {
            momentj.locale("ar")
            return [l.localizeNumber(m.format("DD MMMM YYYY")), l.localizeNumber(m.format("HH:mm"))];
        }
    }, [l, startTime]);

    return (
        <Fragment>
            <div className="main-charts">
                <CursorView l={l} startTime={startTime} endTime={endTime} marginLeft={22} />
                {chartDataList.map(chart =>
                    <div className="scatter-chart-container" key={chart.key}>
                        {React.createElement(chart.icon, { className: "icon" })}
                        <ScatterChart chartKey={chart.key + "-svg"} className={"scatter-chart-svg"}
                            fullWidth={chartWidth - 22} minRectWidth={2} onColor={chart.color}
                            data={chart.data} dataLength={chart.dataLength} />
                    </div>
                )}
                <div className="time-labels with-margin-top" style={{ marginLeft: 22 }}>
                    <div className="normal-text">{startTimeStr[1]}</div>
                    <div className="normal-text">{startTimeStr[0]}</div>
                    <div />
                    <div className="normal-text">{s.deviceReport.now}</div>
                </div>
            </div>
            <div />
            <div className="data-labels">
                {chartDataList.map(chart =>
                    (chart.labelName !== undefined) ? <div key={"label-for-" + chart.key} className={"label-h " + chart.colorName}>
                        <div className="big-text">{l.localizeNumber(chart.count)}</div>
                        <div className="normal-text">{s.deviceReport.time(chart.count)}</div>
                        <div className="normal-text">{s.deviceReport[chart.labelName]}</div>
                    </div> : null
                )}
            </div>
        </Fragment>
    );
}

function CursorView({ l, startTime, endTime, marginLeft, marginRight }) {
    const [left, setLeft] = useState(undefined);
    const [ignoreMouseLeave, setIgnoreMouseLeave] = useState(false); // set true on first touch; just to be sure in mobile 

    const mouse = useCallback((event) => {
        let rect = event.target.getBoundingClientRect();
        let _left = (event.clientX - rect.left) * 100 / rect.width;
        if ((_left >= 0) && (_left <= 100))
            setLeft(_left);
    }, []);

    const touch = useCallback((event) => {
        let touch = event.touches[0];
        let rect = event.target.getBoundingClientRect();
        let _left = (touch.pageX - rect.left) * 100 / rect.width;
        if ((_left >= 0) && (_left <= 100))
            setLeft(_left);
        if (!ignoreMouseLeave)
            setIgnoreMouseLeave(true);
    }, [ignoreMouseLeave]);


    const clear = useCallback(() => {
        if (!ignoreMouseLeave)
            setLeft(undefined);
    }, [ignoreMouseLeave]);

    const labelStr = useMemo(() => {
        const m = momentj.unix(startTime + (left / 100) * (endTime - startTime));
        // return l.localizeNumber(m.format("HH:mm"));

        if (l.language === 'en') {
            momentj.locale("en")
            return [l.localizeNumber(m.format("DD MMMM")), " ", l.localizeNumber(m.format("HH:mm"))];
        } else if (l.language === 'fa') {
            momentj.locale("fa")
            return [l.localizeNumber(m.format("jDD jMMMM")), " ", l.localizeNumber(m.format("HH:mm"))];
        } else if (l.language === 'ar') {
            momentj.locale("ar")
            return [l.localizeNumber(m.format("DD MMMM YYYY")), l.localizeNumber(m.format("HH:mm"))];
        }
    }, [l, startTime, endTime, left]);

    return (
        <div className="cursor-view" style={{ marginLeft, marginRight }}>
            <div className="touch-plane"
                onMouseMove={mouse} onMouseLeave={clear}
                onTouchMove={touch}></div>
            {left !== undefined ? <div className="cursor" style={{ left: String(left) + "%" }}>
                <div className="line"></div>
                <div className="label normal-text">{labelStr}</div>
            </div> : null}
        </div>
    )
}